import React, { useState, useEffect } from 'react';
import { API_URL } from '../services/api';
import {
  Grid,
  Container,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Paper,
  Chip
} from '@mui/material';

import { Link } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MicIcon from '@mui/icons-material/Mic';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import axios from 'axios';

interface NewsItem {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string;
  published_at: string;
  categories: string;
}

const Home: React.FC = () => {
  const [latestNews, setLatestNews] = useState<NewsItem[]>([]);

  useEffect(() => {
    fetchLatestNews();
  }, []);

  const fetchLatestNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news?limit=3`);
      // La API devuelve {news: [...]}
      setLatestNews(response.data?.news || response.data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setLatestNews([]); // Establecer array vacío en caso de error
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Sección Descubre EfectoFM - Arriba */}
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              letterSpacing: '0.1em'
            }}
          >
            DESCUBRE EFECTO<span style={{ color: '#DC143C' }}>FM</span>
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              height: '100%',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
            elevation={3}
          >
            <CalendarMonthIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              PROGRAMACIÓN
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Descubre nuestra parrilla de programas con los mejores DJs y la mejor música
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/programacion"
              fullWidth
            >
              VER PROGRAMACIÓN
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              height: '100%',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
            elevation={3}
          >
            <MicIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              NUESTROS DJs
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Conoce a los mejores DJs de la ciudad que hacen vibrar las ondas
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/djs"
              fullWidth
            >
              CONOCER DJs
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              height: '100%',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
            elevation={3}
          >
            <NewspaperIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              NOTICIAS
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Mantente al día con las últimas noticias del mundo de la música
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/noticias"
              fullWidth
            >
              VER NOTICIAS
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Sección de Últimas Noticias - Abajo */}
      {latestNews.length > 0 && (
        <>
          <Typography
            variant="h4"
            sx={{
              mt: 8,
              mb: 4,
              textAlign: 'center',
              fontWeight: 700,
              letterSpacing: '0.05em'
            }}
          >
            ÚLTIMAS NOTICIAS
          </Typography>

          <Grid container spacing={4}>
            {latestNews.map((news) => (
              <Grid item xs={12} md={4} key={news.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  {news.featured_image && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={`${API_URL}${news.featured_image}`}
                      alt={news.title}
                    />
                  )}
                  <CardContent sx={{ flex: 1 }}>
                    {news.categories && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={news.categories.split(',')[0]}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    )}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {news.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {news.excerpt}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      color="primary"
                      component={Link}
                      to={`/noticias/${news.slug}`}
                    >
                      Leer más
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default Home;